const text = value => (value == null ? '' : String(value).trim())

export function normalizeReport (raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error('The report list contains an invalid record.')
  }
  const id = text(raw.id)
  const companyId = text(raw.company_id)
  if (!id || !companyId)
    throw new Error('A report is missing its id or company_id.')
  const type = text(raw.report_type) || 'Not recorded'
  const code = type.toUpperCase()
  return {
    raw,
    id,
    companyId,
    assetId: text(raw.asset_id),
    number: text(raw.report_number),
    type,
    date: text(raw.report_date),
    techniques: ['RVI', 'CVI'].includes(code) ? [code] : [],
    key: JSON.stringify([companyId, id])
  }
}

// Existing, confirmed storage convention. id identifies the record, not the file.
export function getReportFileName (report) {
  if (!report.number || !report.siteId || !report.assetId) return ''
  const stem =
    report.type.toUpperCase() === 'RVI'
      ? `RVI-${report.siteId}-${report.number}`
      : report.number.replaceAll('/', '_')
  return `${stem}.pdf`
}

// assets/sites are the normalized arrays returned by useScopeOptions.
// Resolve globally within the company, never by assuming the selected site.
export function attachReportLocations (reports, assets, sites) {
  const assetsById = new Map()
  assets.forEach(asset => {
    const id = text(asset.id)
    const list = assetsById.get(id) || []
    list.push(asset)
    assetsById.set(id, list)
  })
  const siteLabels = new Map(sites.map(site => [text(site.id), site.label]))
  return reports.map(report => {
    const candidates = assetsById.get(report.assetId) || []
    const owners = [
      ...new Set(candidates.map(asset => text(asset.siteId)).filter(Boolean))
    ]
    const siteId = owners.length === 1 ? owners[0] : ''
    const asset = siteId
      ? candidates.find(item => text(item.siteId) === siteId)
      : null
    const locationStatus = siteId
      ? 'resolved'
      : owners.length > 1
      ? 'ambiguous'
      : 'missing'
    return {
      ...report,
      siteId,
      locationStatus,
      fileName: getReportFileName({ ...report, siteId }),
      assetLabel: asset?.label || report.assetId || 'Asset not recorded',
      siteLabel: siteId
        ? siteLabels.get(siteId) || siteId
        : locationStatus === 'ambiguous'
        ? 'Asset matches multiple sites'
        : 'Site unavailable'
    }
  })
}

export function reportInScope (row, siteId, assetId, companyId) {
  return (
    row.companyId === text(companyId) &&
    (!siteId || row.siteId === text(siteId)) &&
    (!assetId || row.assetId === text(assetId))
  )
}

export const reportFilters = [
  { key: 'type', label: 'Report type', values: row => [row.type] },
  {
    key: 'technique',
    label: 'Inspection technique',
    values: row => (row.techniques.length ? row.techniques : ['Not recorded'])
  }
]

export function reportSearchText (row) {
  return [
    row.id,
    row.number,
    row.type,
    row.date,
    row.siteId,
    row.assetId,
    row.siteLabel,
    row.assetLabel,
    ...row.techniques
  ]
    .filter(Boolean)
    .join(' ')
}

function dateValue (value) {
  const raw = text(value)
  // The old report loader expects ISO dates; do not guess ambiguous DD/MM dates.
  if (!/^\d{4}-\d{2}-\d{2}(?:$|T)/.test(raw)) return null
  const day = Date.parse(raw.slice(0, 10) + 'T00:00:00Z')
  if (
    !Number.isFinite(day) ||
    new Date(day).toISOString().slice(0, 10) !== raw.slice(0, 10)
  )
    return null
  const result = Date.parse(raw)
  return Number.isFinite(result) ? result : null
}

export function reportDateLabel (value) {
  const raw = text(value)
  return dateValue(raw) === null ? raw || 'Not recorded' : raw.slice(0, 10)
}

export function sortReports (rows, sort, columns) {
  const column = columns.find(item => item.key === sort.key) || columns[0]
  const direction = sort.direction === 'desc' ? -1 : 1
  return [...rows].sort((a, b) => {
    if (column.key === 'date') {
      const left = dateValue(a.date)
      const right = dateValue(b.date)
      // Unparseable/missing dates remain last in both directions.
      if (left === null || right === null) {
        if (left !== right) return left === null ? 1 : -1
        return a.key.localeCompare(b.key)
      }
      return direction * (left - right) || a.key.localeCompare(b.key)
    }
    const comparison = String(column.value(a) ?? '').localeCompare(
      String(column.value(b) ?? ''),
      undefined,
      { numeric: true, sensitivity: 'base' }
    )
    return direction * comparison || a.key.localeCompare(b.key)
  })
}

export function reportExportRows (rows) {
  const fields = [...new Set(rows.flatMap(row => Object.keys(row.raw)))]
  return rows.map(row =>
    Object.fromEntries(
      fields.map(field => {
        const value = row.raw[field]
        return [
          field,
          value == null
            ? ''
            : typeof value === 'object'
            ? JSON.stringify(value)
            : value
        ]
      })
    )
  )
}

// The confirmed link is the exact report number within the company.
// Location metadata is checked for consistency; it does not erase an explicit link.
export function analyzeReportReferences (rows, report) {
  const reference = text(report.number)
  const companyId = text(report.companyId)
  const siteId = text(report.siteId)
  const assetId = text(report.assetId)
  const references = new Set([reference])
  if (text(report.type).toUpperCase() === 'RVI' && siteId) {
    references.add(`RVI-${siteId}-${reference}`)
  }
  const result = {
    matches: [],
    referenceCount: 0,
    missingLocation: 0,
    differentLocation: 0
  }
  if (!reference || !companyId) return result
  rows.forEach(row => {
    if (row.company_id != null && text(row.company_id) !== companyId) return
    if (!references.has(text(row.anomaly_report_id))) return
    result.referenceCount += 1
    result.matches.push(row)
    const rowSite = text(row.site_id)
    const rowAsset = text(row.asset_id)
    if (!rowSite || !rowAsset) result.missingLocation += 1
    else if (rowSite !== siteId || rowAsset !== assetId)
      result.differentLocation += 1
  })
  return result
}

export function anomaliesReferencingReport (rows, report) {
  return analyzeReportReferences(rows, report).matches
}
